import { Button, Popover, Tooltip, Typography } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useState } from "react";
import styled from "styled-components";
import {
  AttendanceFieldsFragment,
  Week,
} from "../../../../../generated/graphql";
import { classInfoStr } from "../../../../../util/classInfo";
import ClassViewerModal from "../../../ClassViewerModal";
import { InitiatedContext } from "../../../ClassAttendanceCard";
import { getParts } from "../AttendanceField/attendanceData";

const { Title, Paragraph } = Typography;

const PopoverContent = styled.div`
  width: 325px;
`;

const Clickable = styled.div`
  padding: 2px;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
`;

type TempAttendances = AttendanceFieldsFragment["tempAttendances"];

/**
 * List items of the other classes visited (temporary attendances) in a week.
 */
const getVisitedClassListItems = (
  tempAttendances: TempAttendances,
  week: Week,
  onViewClass: (classId: number) => void
): JSX.Element[] =>
  tempAttendances
    .map((tempAttendance) => {
      const attendanceDataStr =
        tempAttendance.weekAttendanceInfos.find(
          (weekAttendanceInfo) => weekAttendanceInfo.week === week
        )?.attendanceData ?? "";
      const { attendanceData, comment } = getParts(attendanceDataStr);

      const adjustedDate = attendanceData
        .map((date) => date.adjustedDate)
        .join(", ");
      const dateCommentStr =
        adjustedDate.length > 0 && comment.length > 0
          ? `${adjustedDate} (${comment})`
          : `${adjustedDate}${comment}`;
      if (dateCommentStr.length === 0) {
        return null;
      }

      return (
        <li key={tempAttendance.id}>
          <b>{dateCommentStr}</b> - {classInfoStr(tempAttendance.class)}
          &nbsp;
          <Tooltip title="View class">
            <Button
              icon={<UserOutlined />}
              onClick={() => onViewClass(tempAttendance.class.id)}
            />
          </Tooltip>
        </li>
      );
    })
    .filter((listItem): listItem is JSX.Element => listItem !== null);

/**
 * Shows the details of a student's attendance in a week when clicked: the attendance in the
 * old (legacy) format and any other classes visited.
 */
const AttendanceDetailsPopover: React.FC<{
  week: Week;
  tempAttendances: TempAttendances;
  legacyAttendanceData?: string;
  legacyHint?: string;
  initiatedContext: InitiatedContext;
  depth: number;
}> = ({
  week,
  tempAttendances,
  legacyAttendanceData,
  legacyHint,
  initiatedContext,
  depth,
  children,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [viewClassId, setViewClassId] = useState<number>();

  const visitedClassListItems = getVisitedClassListItems(
    tempAttendances,
    week,
    (classId) => {
      setIsVisible(false);
      setViewClassId(classId);
    }
  );

  if (
    legacyAttendanceData === undefined &&
    visitedClassListItems.length === 0
  ) {
    return <>{children}</>;
  }

  const content = (
    <PopoverContent>
      {legacyAttendanceData !== undefined && (
        <>
          <Title level={5}>Recorded in the old format</Title>
          <Paragraph>
            <code>{legacyAttendanceData || "(empty)"}</code>
          </Paragraph>
          {legacyHint && <Paragraph>{legacyHint}</Paragraph>}
        </>
      )}
      {visitedClassListItems.length > 0 && (
        <>
          <Title level={5}>Other classes visited</Title>
          <Paragraph>
            <ul>{visitedClassListItems}</ul>
          </Paragraph>
        </>
      )}
    </PopoverContent>
  );

  return (
    <>
      <ClassViewerModal
        classId={viewClassId}
        onClose={() => setViewClassId(undefined)}
        initiatedContext={initiatedContext}
        depth={depth + 1}
      />
      <Popover
        content={content}
        trigger="click"
        visible={isVisible}
        onVisibleChange={setIsVisible}
      >
        <Clickable>{children}</Clickable>
      </Popover>
    </>
  );
};

export default AttendanceDetailsPopover;
